import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Box,
  Grid,
  CircularProgress,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PouceIcon from '../../../../assets/pouce.svg'
import SparkIcon from '../../../../assets/Spark.svg'

import { parseISO, isValid, isSameDay } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux'; // Import useSelector and useDispatch
import { getConversationPreviews, getMessagesByConversation, uploadMessageFile, getMessagesPaged, getUnreadMessagesByConversation, searchMessagesPaged } from '../../.././../store/messagesSlice/messagesThunks.js';
import { setSelectedConversationId, setChatPartner, SEND_DIRECT_MESSAGE, clearMessages, SEND_CONVERSATION_READ } from '../../.././../store/messagesSlice/messagesSlice.js';
import { getActiveUsers } from "../../store/interact/interactThunks.js";
import "./MessageHistory.css";
import preferredLanguage from '../../../Auth/store/authSlice.js';
import socketFactory from '../../../../utils/socketFactory.js'
import { getInitialAvatarJSX, splitNames } from '../../../../utils/stringUtils.js';
import EmojiPicker from 'emoji-picker-react';
import SearchIconSvg from "../../../../assets/search.svg";
import EditIconSvg from "../../../../assets/edit_square.svg";
import AddIconSvg from "../../../../assets/add.svg";
import MoodIconSvg from "../../../../assets/mood.svg";
import SendIconSvg from "../../../../assets/send.svg";
import SeenMsgSvg from "../../../../assets/seenMsg.svg";
import ArrowForwardSvg from "../../../../assets/arrow_forward.svg";
import PageSvg from "../../../../assets/Page.svg";
import Popover from "@mui/material/Popover";
import { useTranslation } from "react-i18next";
import MessageHeaderIcon from "../../../../assets/Chat.svg";

const ConversationItem = ({ conversation, selected, onSelect, email, t }) => {
  const isFromOtherUser = conversation.senderOfLastMessage?.email !== email;
  const { userList } = useSelector((state) => state.interactModal);
  const { preferredLanguage } = useSelector((state) => state.auth);
  const { firstName, lastName } = splitNames(conversation.otherUser?.display_name);
  const activeUser = userList.find(
    (u) => u.email === conversation.otherUser?.email
  );
  const isOnline = activeUser?.online;
  return (
    <ListItem
      button
      selected={selected}
      onClick={() => onSelect(conversation.conversationId)}
      className={`conversation-item ${selected ? 'selected' : ''}`}
    >

      <ListItemAvatar
        sx={{
          minWidth: '1.25rem'
        }}>
        <Box className="avatar-wrapper">
          {
            !conversation.otherUser?.urlphoto ||
              conversation.otherUser.urlphoto === 'Avatar.png'
              ? getInitialAvatarJSX(firstName, lastName, {
                className: 'conversation-item__avatar',
                alt: conversation.otherUser?.display_name || 'Utilisateur',
              })
              : (
                <Avatar
                  src={conversation.otherUser.urlphoto}
                  alt={conversation.otherUser?.display_name}
                  className="conversation-item__avatar"
                />
              )
          }
          {isOnline && <span className="avatar-online-dot" />}
        </Box>
      </ListItemAvatar>


      <ListItemText
        primary={
          <Box className="conversation-item__header">
            <span className="conversation-item__name">
              {getDisplayName(conversation.otherUser)}
              {Number(conversation?.otherUser?.is_guest) === 1 && (
                <small>{t("isGuest")}</small>
              )}
            </span>
          </Box>
        }

      />
      {isFromOtherUser && conversation.unreadCount > 0 && (
        <span className="unread-badge">{conversation.unreadCount}</span>
      )}
    </ListItem>
  );
};

const getDisplayName = (user) => {
  if (user?.display_name && user.display_name !== 'Guest') {
    return user.display_name;
  }

  if (!user?.email) {
    return 'Guest';
  }

  return user.email
    .split('@')[0]
    .replace(/[0-9]/g, '')
    .replace(/[^\p{L}]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(
      word =>
        word.charAt(0).toUpperCase() +
        word.slice(1).toLowerCase()
    )
    .join(' ');
};

const UserItem = ({ user, onSelect, t }) => {
  const { firstName, lastName } = splitNames(user?.display_name || user.username || "");
  const isOnline = !!user.online;

  return (
    <ListItem
      button
      onClick={() => onSelect(user)}
      className="conversation-item user-select-item"
    >
      <ListItemAvatar
        sx={{
          minWidth: '1.25rem'
        }}
      >
        <Box className="avatar-wrapper">
          {(!user.urlphoto || user.urlphoto === "Avatar.png")
            ? getInitialAvatarJSX(firstName, lastName, {
              className: "conversation-item__avatar",
              alt: user?.display_name || user.username || "Utilisateur",
            })
            : (
              <Avatar
                src={user.urlphoto}
                alt={user?.display_name || user.username || "Utilisateur"}
                className="conversation-item__avatar"
              />
            )}
          {isOnline && <span className="avatar-online-dot" />}
        </Box>
      </ListItemAvatar>

      <ListItemText
        primary={
          <Box className="conversation-item__header">
            <span className="conversation-item__name">
              {user?.display_name || user?.username || user?.email}
            </span>
          </Box>
        }
        secondary={
          <span className={`user-status-label ${isOnline ? "online" : "offline"}`}>
            {isOnline ? t("online") : t("offline")}
          </span>
        }
      />
    </ListItem>
  );
};

function linkify(text) {
  if (!text) return text;

  const urlRegex = /(https?:\/\/[^\s]+)/g;

  return text.replace(urlRegex, (url) => {
    return `<span class="mh-link-wrapper"><a href="${url}" target="_blank" rel="noopener noreferrer" class="mh-link">${url}</a></span>`;
  });
}

const formatBytes = (bytes) => {
  if (bytes === undefined || bytes === null) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n = n / 1024;
    i++;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const MessageAttachment = ({ file_name, file_type, file_data, file_size }) => {
  const getExt = (file_name, file_type) => {
    const fromName = file_name?.split(".").pop();
    if (fromName) return fromName.toUpperCase();

    const map = {
      "application/pdf": "PDF",
      "application/msword": "DOC",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
      "text/csv": "CSV",
      "application/vnd.ms-excel": "XLS",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
      "application/vnd.ms-powerpoint": "PPT",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
      "application/zip": "ZIP",
      "application/x-zip-compressed": "ZIP",
      "image/jpeg": "JPG",
      "image/png": "PNG",
      "image/webp": "WEBP",
      "image/gif": "GIF",
      "image/tiff": "TIFF",
    };
    return map[file_type] || "FILE";
  };

  const ext = getExt(file_name, file_type);

  const FILE_BADGE = {
    PDF: { bg: "#D92D20" },
    DOC: { bg: "#155EEF" },
    DOCX: { bg: "#155EEF" },
    XLS: { bg: "#079455" },
    XLSX: { bg: "#079455" },
    PPT: { bg: "#F79009" },
    PPTX: { bg: "#F79009" },
    CSV: { bg: "#079455" },
    JPG: { bg: "#479A32" },
    PNG: { bg: "#479A32" },
    WEBP: { bg: "#479A32" },
    GIF: { bg: "#479A32" },
    TIFF: { bg: "#479A32" },
    ZIP: { bg: "#414651" },
  };

  const badgeBg = (FILE_BADGE[ext]?.bg) || "#E5E7EB";

  return (
    <a
      href={file_data}
      download={file_name}
      target="_blank"
      rel="noopener noreferrer"
      className="mh-attach"
    >
      <div className="mh-attach__iconWrap">
        <img src={PageSvg} alt="file" className="mh-attach__icon" />

        <div
          className="mh-attach__badge"
          style={{ "--badge-bg": badgeBg }}
        >
          {ext}
        </div>
      </div>

      <div className="mh-attach__meta">
        <div className="mh-attach__name">{file_name}</div>
        <div className="mh-attach__sub">
          {file_size ? formatBytes(file_size) : (file_type || "")}
        </div>
      </div>
    </a>
  );
};



const MessageItem = ({ message, currentUserID = 1, t }) => {
  const isCurrentUser = message.sender_id === currentUserID;
  const { userList } = useSelector((state) => state.interactModal);
  const { preferredLanguage } = useSelector((state) => state.auth);
  const isRoom = !!(
    message?.roomLabel ||
    message?.room_tenant?.label ||
    message?.roomTenantId ||
    message?.room_tenant_id ||
    message?.isRoomMessage
  );
  const roomLabel =
    message?.roomLabel ??
    message?.room_tenant?.label ??
    message?.room_label ??
    null;
  const senderActive = !isCurrentUser
    ? userList.find((u) => u.email === message.sender?.email)
    : null;
  const isSenderOnline = senderActive?.online;
  return (
    <ListItem
      alignItems="flex-start"
      className={`message-bubble ${isCurrentUser ? 'user' : 'other'}`}
    >
      {!isCurrentUser && (
        <ListItemAvatar
          sx={{
            minWidth: '1.25rem'
          }}>
          <Box className="avatar-wrapper">
            {(() => {
              const { firstName, lastName } = splitNames(message?.sender?.display_name);
              return (
                !message?.sender?.urlphoto || message.sender.urlphoto === 'Avatar.png'
                  ? getInitialAvatarJSX(
                    firstName,
                    lastName,
                    { className: 'message-avatar', alt: message?.sender?.display_name || 'Utilisateur' }
                  )
                  : (
                    <Avatar
                      alt={message.sender?.display_name || "Utilisateur"}
                      src={message.sender.urlphoto}
                      className="message-avatar"
                    />
                  )
              );
            })()}
            {isSenderOnline && <span className="avatar-online-dot" />}
          </Box>
        </ListItemAvatar>
      )}

      <ListItemText
        className="message-content"
        primary={
          <Box>

            <Box className={`msg-header-container ${isCurrentUser ? "me" : "other"}`}>

              <span className="message-author">
                {isCurrentUser ? t("me") : (getDisplayName(message.sender) || t("user"))}
              </span>

              <Box className="msg-time-seen-wrapper">

                <span className="msg-time-header">
                  {message.timestamp &&
                    parseISO(message.timestamp).toLocaleTimeString(preferredLanguage, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                </span>

                {isCurrentUser && message.is_read && (
                  <img
                    src={SeenMsgSvg}
                    alt="seen"
                    className={`msg-seen-icon ${message.is_read ? "seen" : ""}`}
                  />
                )}

              </Box>

            </Box>
            <Box className="message-text-time">

              {message.message &&
                message.message !== "[file]" &&
                message.message !== message.file_url && (
                  <Box
                    className="message-text"
                    dangerouslySetInnerHTML={{ __html: linkify(message.message) }}
                  />
                )}

              {message.file_url && (
                <MessageAttachment
                  file_name={message.file_name}
                  file_type={message.file_type}
                  file_data={message.file_url}
                  file_size={message.file_size}
                />
              )}

              {isRoom && (
                <>
                  <span className="material-symbols-outlined room-inline-icon">
                    groups
                  </span>
                  <Typography variant="caption" className="room-inline-label">
                    {roomLabel || t("room")}
                  </Typography>
                </>
              )}

            </Box>

          </Box>
        }
      />
    </ListItem>
  );
};
const SparkItem = ({ item, currentUserID, t, chatPartner }) => {
  const isCurrentUser = item.sender_id === currentUserID;
  const { preferredLanguage } = useSelector((state) => state.auth);
  const spark = item.spark ?? item.spark_transaction; // fallback
  if (!spark) return null;
  if (isCurrentUser) return null;

  return (
    <ListItem className={`message-bubble ${isCurrentUser ? 'user' : 'other'}`}>
      <ListItemText
        className="message-content"
        primary={
          <Box>
            <Box className={`msg-header-container ${isCurrentUser ? "me" : "other"}`}>
              <span className="msg-time-header">
                {item.timestamp &&
                  parseISO(item.timestamp).toLocaleTimeString(preferredLanguage, {
                    hour: "2-digit", minute: "2-digit",
                  })}
              </span>
            </Box>
            <Box className="message-text spark-item">
              <span className="saprk-container">

                <span className="message-text-sparks">
                  {t('sparks_received_message', { name: chatPartner?.display_name, amount: spark.amount, message: '' })/* Don't show the message here since it's rendered elsewhere */}
                </span>
                <span className="saprk-icon" >
                  <img src={SparkIcon} alt="saprk" className='image-spark' />
                </span>
              </span>
              <span className='spark-message'>
                {spark.message}
              </span>
            </Box>
          </Box>
        }
      />
    </ListItem>
  );
};

const EncouragementItem = ({ item, currentUserID, t, chatPartner }) => {
  const isCurrentUser = item.sender_id === currentUserID;

  const { preferredLanguage } = useSelector((state) => state.auth);
  const enc = item.encouragement;
  if (isCurrentUser) return null;
  if (!enc) return null;

  return (
    <ListItem className={`message-bubble ${isCurrentUser ? 'user' : 'other'}`}>
      <ListItemText
        className="message-content"
        primary={
          <Box>
            <Box className={`msg-header-container ${isCurrentUser ? "me" : "other"}`}>

              <span className="msg-time-header">
                {item.timestamp &&
                  parseISO(item.timestamp).toLocaleTimeString(preferredLanguage, {
                    hour: "2-digit", minute: "2-digit",
                  })}
              </span>
            </Box>
            <Box className="encouragement-item">
              <span className="encouragement-icon" >
                <img src={PouceIcon} alt="en" className="encouragement-image" />
              </span>
              <span className="message-text-sparks">
                {t('encouragement_received_message', { name: chatPartner?.display_name })}
              </span>
            </Box>

          </Box>
        }
      />
    </ListItem>
  );
};
const DateSeparator = ({ date }) => (
  <Typography variant="body2" className="date-separator">
    <span className="material-symbols-outlined">
      calendar_today
    </span>
    <span>{date.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
  </Typography>
);





export default function MessageHistory({ currentUserID, localAvatar, onClose, }) {

  const initRef = useRef(false);
  const bottomRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const lastMsgIdRef = useRef(null);
  const {
    chatPartner,
    messageLoading,
    groupedMessages,
    selectedConversationId,
    localConversations,
    error,
  } = useSelector((state) => state.messageHistory);

  const email = localAvatar.email
  const dispatch = useDispatch();
  const { openModal } = useSelector((state) => state.controlPanel);
  const isOpen = openModal === 'message_history';
  const [messageText, setMessageText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const { isRoom, target } = useSelector((state) => state.messageHistory);
  const { mapConfig } = useSelector((state) => state.MapDisplay);
  const { userList } = useSelector(state => state.interactModal);
  const { avatars } = useSelector((state) => state.avatars);
  const activeUser = userList.find(u => u.email === chatPartner?.email);
  const isOnline = activeUser?.online;
  const [isSelectingUser, setIsSelectingUser] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [hasScrolledOnOpen, setHasScrolledOnOpen] = useState(false);
  const isSearchMode = (searchQuery || "").trim().length > 0;
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const isFetchingRef = useRef(false);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const emojiOpen = Boolean(emojiAnchor);
  const { t } = useTranslation();
  const [pendingFile, setPendingFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [sendError, setSendError] = useState(null);
  const USER_PAGE_SIZE = 10;
  const userScrollRef = useRef(null);
  const userPageRef = useRef(1);
  const [userPage, setUserPage] = useState(1);

  const handleSend = async () => {
    const hasText = !!messageText.trim();
    const hasFile = !!pendingFile;

    if (!hasText && !hasFile) return;
    if (!chatPartner?.email) return;

    exitSearchModeToNormal();

    try {
      setIsUploading(true);

      if (hasFile) {
        const formData = new FormData();
        formData.append("file", pendingFile);

        const result = await dispatch(uploadMessageFile({ body: formData })).unwrap();

        dispatch(SEND_DIRECT_MESSAGE({
          sender: localAvatar.email,
          recipientEmail: chatPartner.email,
          message: messageText || "",
          fileName: result.fileName,
          fileType: result.fileType,
          fileUrl: result.url,
          fileSize: result.fileSize,
          map_id: mapConfig.mapId,
          tenantId: localAvatar.tenant_id
        }));

        setPendingFile(null);
        setMessageText("");
        return;
      }

      dispatch(SEND_DIRECT_MESSAGE({
        sender: localAvatar.email,
        recipientEmail: chatPartner.email,
        message: messageText,
        map_id: mapConfig.mapId
      }));

      setMessageText("");
    } catch (err) {
      setSendError(t("FILE_TYPE_NOT_ALLOWED"));
    }
    finally {
      setIsUploading(false);
      resetTextarea();
    }
  };

  const resetTextarea = () => {
    const textarea = inputRef.current;
    if (!textarea) return;

    textarea.style.height = "2.7675rem";
    textarea.style.overflowY = "hidden";
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    exitSearchModeToNormal();
    setPendingFile(file);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleStartConversationWithUser = (user) => {
    setSearchQuery("");
    resetPaging();
    const existingConv = localConversations.find(
      (conv) => conv.otherUser?.email === user.email
    );

    const online = !!user.online;
    dispatch(clearMessages());

    if (existingConv) {
      dispatch(setSelectedConversationId(existingConv.conversationId));
      dispatch(setChatPartner(existingConv.otherUser));
    } else {
      dispatch(setSelectedConversationId(null));
      dispatch(setChatPartner({
        display_name: user?.display_name || user?.username || user?.email,
        email: user.email,
        urlphoto: user.urlphoto,
      }));
    }
  };

  const openNewDiscussion = useCallback(() => {
    setIsSelectingUser(true);
    dispatch(getActiveUsers());
    setSearchTerm("");
    setUserPage(1);
  }, [dispatch]);


  const toggleRightPanelMsg = () => {
    setLeftPanelOpen(prev => !prev);
  }

  useEffect(() => {
    if (isSelectingUser) setUserPage(1);
  }, [searchTerm, isSelectingUser]);


  const getPartnerFirstName = (partner) => {
    if (!partner) return "";
    const { firstName } = splitNames(
      partner?.display_name || partner?.username || partner?.email || ""
    );
    return firstName || partner?.display_name || partner?.username || partner?.email;
  };

  const resetPaging = useCallback(() => {
    isFetchingRef.current = false;
    setPage(1);
    setPagination(null);
  }, []);

  //Pagination - recherche messages
  const fetchPage = useCallback(
    (pageToLoad, q) => {
      if (!selectedConversationId) return Promise.resolve(null);

      const action = (q || "").trim()
        ? searchMessagesPaged({
          params: [{ conversationId: selectedConversationId }],
          options: { query: { q, page: String(pageToLoad), size: 20 } },
        })
        : getMessagesPaged({
          params: [{ conversationId: selectedConversationId }],
          options: { query: { page: String(pageToLoad), size: 20 } },
        });

      return dispatch(action)
        .unwrap()
        .then((res) => {
          setPagination(res.pagination);
          return res;
        });
    },
    [dispatch, selectedConversationId]
  );
  //Scroll auto jusqu’en bas
  const scrollToBottom = useCallback((behavior = "auto") => {
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ block: "end", behavior });
    });
  }, []);

  const loadFirstPage = useCallback(
    (q) => {
      if (!selectedConversationId) return;
      resetPaging();
      setSearchQuery(q);

      fetchPage(1, q).then(() => {
        scrollToBottom("auto");
      });
    },
    [selectedConversationId, resetPaging, fetchPage, scrollToBottom]
  );

  const exitSearchModeToNormal = useCallback(() => {
    if (!selectedConversationId) return;
    if (!searchQuery.trim()) return;
    loadFirstPage("");
  }, [selectedConversationId, searchQuery, loadFirstPage]);

  const handleMessageSearch = useCallback(
    (value) => {
      if (!selectedConversationId) {
        setSearchQuery(value);
        return;
      }
      loadFirstPage(value);
    },
    [selectedConversationId, loadFirstPage]
  );

  const normalizeName = (u) =>
    (u?.display_name || u?.username || u?.email || "").toLowerCase().trim();

  const usersForNewDiscussion = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const base = userList
      .filter((u) => u.email !== email)
      .filter((u) => {
        if (!term) return true;
        const name = normalizeName(u);
        const mail = (u.email || "").toLowerCase();
        return name.includes(term) || mail.includes(term);
      });

    return [...base].sort((a, b) => {
      const ao = a.online ? 1 : 0;
      const bo = b.online ? 1 : 0;
      if (bo !== ao) return bo - ao;

      return normalizeName(a).localeCompare(normalizeName(b), undefined, {
        sensitivity: "base",
        numeric: true,
      });
    });
  }, [userList, email, searchTerm]);

  //Faire la pagination UI
  const pagedUsersForNewDiscussion = React.useMemo(() => {
    return usersForNewDiscussion.slice(0, userPage * USER_PAGE_SIZE);
  }, [usersForNewDiscussion, userPage]);

  useEffect(() => {
    if (!isOpen) {
      dispatch(setSelectedConversationId(null));
      dispatch(clearMessages());
      dispatch(setChatPartner(null));
      setHasScrolledOnOpen(false);
      setSearchQuery("");
      resetPaging();
      return;
    }

    setHasScrolledOnOpen(false);
    dispatch(clearMessages());
    dispatch(setChatPartner(null));
    dispatch(getActiveUsers());
    dispatch(getConversationPreviews());
    dispatch(getUnreadMessagesByConversation());
  }, [isOpen, dispatch, resetPaging]);

  useEffect(() => {
    if (!isOpen || !selectedConversationId) return;
    setHasScrolledOnOpen(false);
    loadFirstPage("");
  }, [isOpen, selectedConversationId, loadFirstPage]);

  useEffect(() => {
    if (!isOpen) return;
    if (!selectedConversationId) return;
    if (hasScrolledOnOpen) return;
    if (!groupedMessages || groupedMessages.length === 0) return;

    scrollToBottom("auto");
    setHasScrolledOnOpen(true);
  }, [isOpen, selectedConversationId, groupedMessages?.length, hasScrolledOnOpen, scrollToBottom]);

  //scroll messages
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const handleScroll = () => {
      if (
        el.scrollTop < 100 &&
        pagination?.hasMore &&
        !messageLoading &&
        !isFetchingRef.current
      ) {
        isFetchingRef.current = true;
        setPage((p) => p + 1);
      }
      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setIsNearBottom(distanceToBottom < 120);
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [pagination, messageLoading]);

  //lorsque page augmente garder la position scroll
  useEffect(() => {
    if (page === 1) return;
    if (!selectedConversationId) return;

    const container = listRef.current;
    if (!container) return;

    const prevHeight = container.scrollHeight;

    fetchPage(page, searchQuery)
      .then(() => {
        requestAnimationFrame(() => {
          const el = listRef.current;
          if (!el) return;
          const newHeight = el.scrollHeight;
          el.scrollTop = newHeight - prevHeight;
        });
      })
      .finally(() => {
        isFetchingRef.current = false;
      });
  }, [page, selectedConversationId, searchQuery, fetchPage]);

  //mark as read + auto scroll
  useEffect(() => {
    if (!isOpen || !selectedConversationId) return;
    if (!groupedMessages || groupedMessages.length === 0) return;

    const lastMessageItem = [...groupedMessages]
      .reverse()
      .find((item) => item.type === "message");

    const lastMsg = lastMessageItem?.message;
    const lastId = lastMsg?.id;
    if (!lastId) return;

    const prevLastId = lastMsgIdRef.current;
    const isNew = lastId !== prevLastId;
    lastMsgIdRef.current = lastId;
    if (isNew && isNearBottom) {
      scrollToBottom("smooth");
    }

    const alreadyRead = lastMsg?.is_read;
    const senderEmail = lastMsg?.sender?.email;

    const isFromOther =
      senderEmail &&
      email &&
      senderEmail.toLowerCase() !== email.toLowerCase();

    if (!alreadyRead && isFromOther) {
      dispatch(
        SEND_CONVERSATION_READ({
          conversationId: selectedConversationId,
          readerEmail: email,
        })
      );
    }
  }, [
    groupedMessages,
    isOpen,
    selectedConversationId,
    isNearBottom,
    email,
    dispatch,
    scrollToBottom,
  ]);

  useEffect(() => {
    if (isOpen) dispatch(getActiveUsers());
  }, [avatars, isOpen, dispatch]);


  useEffect(() => {
    userPageRef.current = userPage;
  }, [userPage]);

  //scroll users
  useEffect(() => {
    if (!isSelectingUser) return;
    const el = userScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
      const current = userPageRef.current;

      if (nearBottom && current * USER_PAGE_SIZE < usersForNewDiscussion.length) {
        setUserPage(current + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [isSelectingUser, usersForNewDiscussion.length]);


  function useMobile() {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 980);

    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 980);
      };

      window.addEventListener('resize', handleResize);

      // Nettoyage
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
  }

  const isMobile = useMobile();

  const [leftPanelOpen, setLeftPanelOpen] = useState(true);


  useEffect(() => {
    if (target) {
      if (localConversations && localConversations.length > 0) {
        const conv = localConversations.find(conv => conv.otherUser?.email === target.email)
        if (conv) {
          dispatch(setSelectedConversationId(conv.conversationId))
          dispatch(setChatPartner(conv.otherUser));

        } else {
          dispatch(setChatPartner({
            display_name: target.display_name || target.username || target.email,
            email: target.email,
            urlphoto: target.urlphoto,
          }));
        }
      }

    } else {
      dispatch(setSelectedConversationId(localConversations[0]?.conversationId))
      dispatch(setChatPartner(localConversations[0]?.otherUser));
    }
  }, []);

  const conversationsWithTarget = React.useMemo(() => {
    let list = [...localConversations];

    if (target) {
      const exists = list.some(
        (conv) => conv.otherUser?.email === target.email
      );

      if (!exists) {
        const fakeConversation = {
          conversationId: null,
          otherUser: {
            display_name: target.display_name,
            email: target.email,
            urlphoto: target.urlphoto,
          },
          lastMessage: "",
          lastTimestamp: new Date().toISOString(),
          senderOfLastMessage: {
            email: null
          },
          isFile: false,
          fileName: null,
          unreadCount: 0,
          is_read: true
        };

        list = [fakeConversation, ...list];
      }
    }

    return list;
  }, [localConversations, target]);

  const handlePreviousChat = () => {
    if (isMobile) {
      dispatch(setSelectedConversationId(null))
      dispatch(setChatPartner(null));
    }
    else {
      setLeftPanelOpen(true)
    }
  }

  const handleChange = (e) => {
    const textarea = inputRef.current;

    textarea.style.height = "auto";
    textarea.style.height = textarea.scrollHeight + "px";

    setMessageText(e.target.value);
  };

  /***************************Socket*************************************/



  /***************************Socket*************************************/

  return (
    <>
      <div className='msg-container'>
        <div className="title-dialog">
          <h2>
            <img src={MessageHeaderIcon} alt="msg" />
            {t("message_history")}
          </h2>

          <span className="material-symbols-outlined cursor-pointer" onClick={onClose}>
            close
          </span>
        </div>
        <div className={`dialog-content ${!leftPanelOpen ? "hide-conv-list" : ""}`}>
          <Grid container className='grid-container' sx={{ height: '100%' }}>
            {/* Liste des conversations */}
            <Grid item className={`conversation-list ${(selectedConversationId || chatPartner) ? 'has-conversation-selected' : ''}`} sx={{ pr: "0" }}>
              {isSelectingUser && (
                <Box className="new-discussion-header">
                  <button
                    type="button"
                    className="back-btn"
                    onClick={() => {
                      setIsSelectingUser(false);
                      setSearchTerm("");
                    }}
                  >
                    <img src={ArrowForwardSvg} alt="back" className="back-icon" />
                  </button>

                  <span className="new-discussion-title">{t("new_discussion")}</span>
                </Box>
              )}

              <Box className={isSelectingUser ? "userSearchRow" : "convSearchRow"}>
                <Box className={isSelectingUser ? "userSearchBox" : "convSearchBox"}>
                  <input
                    className={isSelectingUser ? "userSearchInput" : "convSearchInput"}
                    placeholder={t("search_for_a_user")}

                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <img
                    src={SearchIconSvg}
                    alt="search"
                    className="conversation-search-icon"
                  />
                </Box>

                <button
                  type="button"
                  className="right_panel_open-msg"
                  onClick={toggleRightPanelMsg}
                >
                  <span class="material-symbols-outlined">
                    right_panel_open
                  </span>
                </button>

              </Box>
              {!isSelectingUser && (
                <button
                  type="button"
                  className="conversation-edit-btn"
                  onClick={openNewDiscussion}
                >
                  <img src={EditIconSvg} alt="edit" className="conversation-edit-icon" />
                  Nouveau message
                </button>
              )}
              <Box className="conversation-scroll" ref={userScrollRef}>
                <List>
                  {/* <Divider className="sep-conversation" /> */}

                  {isSelectingUser
                    ? (
                      pagedUsersForNewDiscussion.map((user) => (
                        <React.Fragment key={user.email}>
                          <UserItem
                            user={user}
                            onSelect={handleStartConversationWithUser}
                            t={t}
                          />
                          {/* <Divider
                            className="sep-conversation"
                            variant="inset"
                            component="li"
                          /> */}
                        </React.Fragment>
                      ))
                    )
                    : (
                      [...conversationsWithTarget]
                        .filter((conv) => {
                          if (!searchTerm.trim()) return true;
                          const term = searchTerm.toLowerCase();
                          const name = (conv.otherUser?.display_name || "").toLowerCase();
                          const lastMsg = (conv.lastMessage || "").toLowerCase();
                          return name.includes(term) || lastMsg.includes(term);
                        })
                        .sort((a, b) => new Date(b.lastTimestamp) - new Date(a.lastTimestamp))
                        .map((conv) => (
                          <React.Fragment key={conv.conversationId}>

                            <ConversationItem
                              conversation={conv}
                              selected={conv.conversationId === selectedConversationId}
                              email={email}
                              t={t}
                              onSelect={(id) => {
                                dispatch(setSelectedConversationId(id));

                                const partner = conv.otherUser;
                                const active = userList.find(u => u.email === partner.email);

                                dispatch(setChatPartner(partner));
                              }}
                            />
                            {/* <Divider
                              className="sep-conversation"
                              variant="inset"
                              component="li"
                            /> */}
                          </React.Fragment>
                        ))
                    )}
                </List>
              </Box>
            </Grid>

            {(!isMobile || selectedConversationId || chatPartner) &&
              <Grid item sx={{ pl: "0" }} className="conversation-withId">
                {error && (
                  <Box p={2} color="error.main">
                    {error}
                  </Box>
                )}
                {!chatPartner && !messageLoading && (
                  <Box className="empty-chat-container">

                    <Box className="empty-chat-icon-wrapper">
                      <img src={SendIconSvg} className="empty-chat-icon" />
                    </Box>

                    <Typography variant="h6" className="empty-chat-title">
                      {t("welcome_to_chat")}
                    </Typography>

                    <Typography variant="body2" className="empty-chat-subtitle">
                      {t("empty_chat_subtitle")}
                    </Typography>

                    <button
                      type="button"
                      className="empty-chat-button"
                      onClick={openNewDiscussion}
                    >
                      {t("start_discussion")}
                    </button>

                  </Box>
                )}

                {chatPartner && (
                  <Box className="conversation-right-panel">
                    {chatPartner && (
                      <Box className="chat-header">
                        <Box className="chat-header-left">
                          {/* <span onClick={() => dispatch(setSelectedConversationId(null))} class="chat-liste-mob material-symbols-outlined">
                            arrow_left_alt
                          </span> */}
                          <span onClick={handlePreviousChat} class="chat-liste-mob material-symbols-outlined">
                            right_panel_close
                          </span>
                          <Box className="chat-avatar-wrapper">
                            {(!chatPartner?.urlphoto ||
                              chatPartner.urlphoto === "Avatar.png") ? (
                              getInitialAvatarJSX(
                                splitNames(chatPartner?.display_name).firstName,
                                splitNames(chatPartner?.display_name).lastName,
                                {
                                  className: "chat-header-avatar",
                                  alt: chatPartner?.display_name,
                                }
                              )
                            ) : (
                              <Avatar
                                src={chatPartner?.urlphoto}
                                alt={chatPartner?.display_name}
                                className="chat-header-avatar"
                              />
                            )}
                            {isOnline && (
                              <span className="avatar-online-dot"></span>
                            )}
                          </Box>
                          <Box>
                            <div className="chat-header-name">
                              {
                                console.log('zaniba', chatPartner)
                              }
                              {chatPartner?.display_name}
                            </div>
                            <div className={`chat-header-status ${isOnline ? "" : "offline"}`}>
                              {isOnline ? t("active_now") : t("offline")}
                            </div>
                          </Box>
                        </Box>

                        <Box className="chat-header-right">
                          <input
                            className="chat-header-search-input"
                            placeholder={t("search_message")}
                            value={searchQuery}
                            onChange={(e) => handleMessageSearch(e.target.value)}
                          />

                          <img
                            src={SearchIconSvg}
                            alt="search"
                            className="chat-header-search-icon"
                          />

                        </Box>
                      </Box>
                    )}

                    <div className="message-container" ref={listRef}>
                      {messageLoading && <div className="loading-overlay"><CircularProgress size={24} /></div>}
                      {groupedMessages.length === 0 ? (
                        messageLoading ? (
                          <div ref={bottomRef} style={{ height: "100%" }} />
                        ) :
                          isSearchMode ? (
                            <div ref={bottomRef}></div>
                          ) : (
                            <Box className="start-chat-container">
                              <Box className="start-chat-icon-wrapper">
                                <img src={SendIconSvg} className="start-chat-icon" />
                              </Box>

                              <Typography variant="h6" className="start-chat-title">
                                {t("start_chat_title")}
                              </Typography>

                              <Typography variant="body2" className="start-chat-subtitle">
                                {t("start_chat_subtitle", { name: getPartnerFirstName(chatPartner) })}
                              </Typography>
                            </Box>
                          )
                      ) : (
                        <>
                          <List className="message-container-list">
                            {groupedMessages.map((item, index) =>
                              item.type === "date" ? (
                                <DateSeparator
                                  key={`date-${item.date instanceof Date
                                    ? item.date.toISOString()
                                    : parseISO(item.date).toISOString()}`}
                                  date={item.date instanceof Date ? item.date : parseISO(item.date)}
                                />
                              ) : item.type === "spark" ? (
                                <SparkItem
                                  key={item.message.id}
                                  item={item.message}
                                  currentUserID={currentUserID}
                                  chatPartner={chatPartner}
                                  t={t}
                                />
                              ) : item.type === "encouragement" ? (
                                <EncouragementItem
                                  key={item.message.id}
                                  item={item.message}
                                  currentUserID={currentUserID}
                                  chatPartner={chatPartner}

                                  t={t}
                                />
                              ) : (
                                <MessageItem
                                  key={item.message.id}
                                  message={item.message}
                                  currentUserID={currentUserID}
                                  t={t}
                                />
                              )
                            )}
                          </List>
                          <div ref={bottomRef}></div>
                        </>

                      )}
                    </div>

                    <Box className="figma-chat-wrapper">

                      <Box className="figma-chat-inner">

                        <IconButton className="figma-add-btn" component="label" disabled={isUploading || !chatPartner}>
                          <img src={AddIconSvg} alt="add" className="figma-add-icon" />
                          <input ref={fileInputRef} type="file" hidden onChange={handleFileUpload} />
                        </IconButton>



                        <Box className="figma-input-wrapper">
                          {pendingFile && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                                px: 1,
                                py: 0.5,
                                borderRadius: "0.5rem",
                                background: "#EEF2F6",
                              }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                                attach_file
                              </span>

                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {pendingFile.name}
                              </span>

                              <IconButton size="small" onClick={() => setPendingFile(null)}>
                                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                                  close
                                </span>
                              </IconButton>
                            </Box>
                          )}

                          {/*<input
                            ref={inputRef}
                            type="text"
                            className="figma-input"
                            placeholder={t("message")}
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            onKeyDown={handleKeyDown}
                          />*/}

                          <textarea
                            ref={inputRef}
                            className="figma-input"
                            placeholder={t("message")}
                            value={messageText}
                            onChange={(e) => {
                              const textarea = e.target;

                              textarea.style.height = "auto";

                              if (textarea.scrollHeight > 250) {
                                textarea.style.height = "250px";
                                textarea.style.overflowY = "auto";
                              } else {
                                textarea.style.height = (textarea.scrollHeight - 15) + "px";
                                textarea.style.overflowY = "hidden";
                              }

                              setMessageText(textarea.value);
                            }}
                            onKeyDown={handleKeyDown}
                          />

                          <img
                            src={MoodIconSvg}
                            alt="emoji"
                            className="figma-input-emoji-icon"
                            onClick={(e) => setEmojiAnchor(emojiOpen ? null : e.currentTarget)}
                          />
                        </Box>

                        <IconButton
                          className="figma-send-btn"
                          onClick={handleSend}
                          disabled={isUploading || (!messageText.trim() && !pendingFile)}
                        >
                          <img src={SendIconSvg} alt="send" className="figma-send-icon" />
                        </IconButton>

                      </Box>

                      <Popover
                        open={emojiOpen}
                        anchorEl={emojiAnchor}
                        onClose={() => setEmojiAnchor(null)}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                      >
                        <EmojiPicker
                          onEmojiClick={(emojiData) => {
                            setMessageText((prev) => prev + emojiData.emoji);
                            setEmojiAnchor(null);
                            requestAnimationFrame(() => inputRef.current?.focus());
                          }}
                        />
                      </Popover>

                    </Box>
                  </Box>
                )}
              </Grid>
            }

          </Grid>
        </div>
      </div>
    </>
  );
}

